import React from "react";

/**
 * Global React error boundary — catches uncaught render errors so the app
 * doesn't go fully white. Logs to console + shows a friendly Greek fallback
 * with a copy-stack-trace button so the user can send the error to the dev.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    this.setState({ info });
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary] Uncaught render error:", error, info);
  }

  copyStack = () => {
    const text =
      (this.state.error?.stack || String(this.state.error)) +
      "\n\n--- Component stack ---\n" +
      (this.state.info?.componentStack || "");
    try {
      navigator.clipboard.writeText(text);
      alert("Το stack trace αντιγράφηκε. Στείλε το στον dev για διάγνωση.");
    } catch {
      alert(text);
    }
  };

  hardReload = () => {
    // Force full reload — Safari sometimes serves stale chunks after deploy
    if (window.location.reload) window.location.reload(true);
  };

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "linear-gradient(135deg, #1E3A5F 0%, #0EA5E9 100%)",
          padding: 24,
          fontFamily: "system-ui, -apple-system, Arial, sans-serif",
        }}
        data-testid="error-boundary"
      >
        <div
          style={{
            maxWidth: 560,
            width: "100%",
            background: "white",
            borderRadius: 18,
            padding: "32px 26px",
            boxShadow: "0 18px 48px rgba(0,0,0,0.22)",
            textAlign: "center",
          }}
        >
          <div style={{ fontSize: 52, color: "#DC2626", lineHeight: 1, marginBottom: 8 }}>⚠️</div>
          <h1 style={{ color: "#1E3A5F", fontSize: 22, margin: "0 0 12px" }}>
            Κάτι πήγε στραβά στη σελίδα
          </h1>
          <p style={{ color: "#475569", fontSize: 14, lineHeight: 1.6, margin: "0 0 18px" }}>
            Η εφαρμογή σταμάτησε να φορτώνει αυτή τη σελίδα. Συνήθως αυτό
            φτιάχνεται με **σκληρό refresh**. Αν συνεχίζει, αντίγραψε το stack trace
            και στείλ' το στον dev.
          </p>
          <div
            style={{
              background: "#FEF2F2",
              border: "1px solid #FCA5A5",
              borderRadius: 8,
              padding: "10px 12px",
              fontSize: 12,
              color: "#7F1D1D",
              fontFamily: "monospace",
              textAlign: "left",
              overflowX: "auto",
              marginBottom: 18,
              maxHeight: 160,
              overflowY: "auto",
            }}
          >
            {String(this.state.error)}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <button
              onClick={this.hardReload}
              style={{
                background: "#0EA5E9",
                color: "white",
                border: "none",
                borderRadius: 10,
                padding: "12px 16px",
                fontSize: 14,
                fontWeight: 700,
                cursor: "pointer",
              }}
              data-testid="error-boundary-reload"
            >
              ↻ Σκληρή ανανέωση σελίδας
            </button>
            <button
              onClick={this.copyStack}
              style={{
                background: "#F1F5F9",
                color: "#1E3A5F",
                border: "1px solid #CBD5E1",
                borderRadius: 10,
                padding: "10px 16px",
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
              }}
              data-testid="error-boundary-copy"
            >
              📋 Αντιγραφή stack trace
            </button>
            <button
              onClick={() => (window.location.href = "/")}
              style={{
                background: "transparent",
                color: "#64748B",
                border: "none",
                padding: "8px 12px",
                fontSize: 12,
                cursor: "pointer",
              }}
            >
              Επιστροφή στην αρχική
            </button>
          </div>
        </div>
      </div>
    );
  }
}
